/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, createContext, useContext, useEffect, useState} from 'react'
import {WithChildren} from '../../helpers'
import { useLocation, useParams} from 'react-router-dom'
import Layout from './Layout'
import { useAuth } from 'app/modules/auth'

export interface PageLink {
  title: string
  path: string
  isActive: boolean
  isSeparator?: boolean
}

export interface PageDataContextModel {
  pageTitle?: string
  setPageTitle: (_title: string) => void
  pageDescription?: string
  setPageDescription: (_description: string) => void
  pageBreadcrumbs?: Array<PageLink>
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageTitle: (_title: string) => {},
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
})

const PageDataProvider: FC<WithChildren> = ({children}) => {
  const [pageTitle, setPageTitle] = useState<string>('')
  const [pageDescription, setPageDescription] = useState<string>('')
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([])

  const value: PageDataContextModel = {
    pageTitle,
    setPageTitle,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
  }
  return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>
}

function usePageData() {
  return useContext(PageDataContext)
}

type Props = {
  description?: string
  breadcrumbs?: Array<PageLink>
  title?: string
  icon?: string
  desc?: string
  domainType?: string
  domainId?: string
}

const DomainPageTitle: FC<Props & WithChildren> = ({
  children,
  description,
  breadcrumbs,
  title,
  icon,
  desc,
  domainType,
  domainId,
}) => {
  const {setPageTitle, setPageDescription, setPageBreadcrumbs} = usePageData()
  const location = useLocation()
  const {id} = useParams()
  const {currentUser} = useAuth()
  
  const getDomainTabs = () => {
    const currentId = domainId || id
    
    if (currentId) {
      return [
        {
          title: 'Issues',
          slug: 'issues',
          href: `/vulnerabilities/domains/${currentId}`,
        },
        // {
        //   title: 'Assets',
        //   slug: 'assets',
        //   href: `/clouds/${currentId}/assets`,
        // },
        // {
        //   title: 'Containers',
        //   slug: 'containers',
        //   href: `/clouds/${currentId}/containers`,
        // },
        {
          title: 'Checks',
          slug: 'checks',
          href: `/vulnerabilities/domains/${currentId}/checks`,
        }
      ]
    } else {
      return [
        {
          title: 'Overview',
          slug: 'overview',
          href: '/vulnerabilities/domains',
        },
        {
          title: 'Checks',
          slug: 'checks',
          href: '/vulnerabilities/domains/checks',
        },
        // {
        //   title: 'Assets',
        //   slug: 'assets',
        //   href: '/clouds/assets',
        // },
        // {
        //   title: 'Containers',
        //   slug: 'containers',
        //   href: '/clouds/containers',
        // }
      ]
    }
  }

  const tabs = getDomainTabs()
  
  const [activeTab, setActiveTab] = useState(
    (domainId || id) ? 'issues' : 'overview'
  )
  useEffect(() => {
    if (children) {
      setPageTitle(children.toString())
    }
    return () => {
      setPageTitle('')
    }
  }, [children])

  useEffect(() => {
    if (description) {
      setPageDescription(description)
    }
    return () => {
      setPageDescription('')
    }
  }, [description])

  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs)
    }
    return () => {
      setPageBreadcrumbs([])
    }
  }, [breadcrumbs])

  useEffect(() => {
    const currentId = domainId || id
    const currentDomainType = domainType?.toLowerCase() || 'gcp'
    
    if (location.pathname.includes(`${currentDomainType}/configuration`)) {
      setActiveTab('')
    } else if (currentId) {
      if (location.pathname.includes('checks')) {
        setActiveTab('checks')
      } else if(location.pathname.includes('assets')) {
        setActiveTab('assets')
      } else if(location.pathname.includes('containers')) {
        setActiveTab('containers')
      } else {
        setActiveTab('issues')
      }
    } else {
      if (location.pathname.includes('checks')) {
        setActiveTab('checks')
      } else if(location.pathname.includes('assets')) {
        setActiveTab('assets')
      } else if(location.pathname.includes('containers')) {
        setActiveTab('containers')
      } else {
        setActiveTab('overview')
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, domainId, domainType, location.pathname])


  return (
    <>
    {icon ? (
      <Layout
        icon={icon}
        title={title}
        desc={desc}
        tabs={(currentUser?.account_type === 'internal' || currentUser?.account_type === 'Trial-plus') && !location.pathname.includes(`${domainType?.toLowerCase() || 'gcp'}/configuration`) ? tabs : []}
        activeTab={currentUser?.account_type === 'internal' || currentUser?.account_type === 'Trial-plus' ? activeTab : ''}
      />
    ) : null}
  </>
  )
}

const PageDescription: FC<WithChildren> = ({children}) => {
  const {setPageDescription} = usePageData()
  useEffect(() => {
    if (children) {
      setPageDescription(children.toString())
    }
    return () => {
      setPageDescription('')
    }
  }, [children])
  return <></>
}

export {PageDescription, DomainPageTitle, PageDataProvider, usePageData}
